import { niceDateTimeWithWeek } from "../../micro/fns/util";

export const getSxPriceEstimatePdfDefinition = (data) => {
  const {
    payor,
    anesthesiaType,
    anesthesiaRate,
    surgeries,
    consolidatedTotal,
    hospitalName = "Eye Hospital",
    patientName,
    patientId,
  } = data;

  const docDefinition = {
    pageSize: "A4",
    pageMargins: [40, 70, 40, 60],
    header: {
      margin: [40, 25, 40, 15],
      stack: [
        {
          columns: [
            {
              width: "*",
              stack: [
                {
                  text: hospitalName ? hospitalName.toUpperCase() : "DISHA EYE HOSPITALS",
                  style: "hospitalName",
                },
                // {
                //   text: "Private Limited",
                //   style: "hospitalSubtitle",
                // },
              ],
            },
            {
              width: "auto",
              text: "SURGERY COST ESTIMATE",
              style: "mainHeader",
              alignment: "right",
            },
          ],
        },
        {
          canvas: [
            {
              type: "line",
              x1: 0,
              y1: 5,
              x2: 515,
              y2: 5,
              lineWidth: 2,
              lineColor: "#137cbd",
            },
          ],
        },
      ],
    },
    footer: (currentPage, pageCount) => {
      return {
        margin: [40, 10, 40, 0],
        columns: [
          {
            text: `Generated: ${niceDateTimeWithWeek(new Date())}`,
            style: "footerText",
          },
          {
            text: `Page ${currentPage} of ${pageCount}`,
            style: "footerText",
            alignment: "right",
          },
        ],
      };
    },
    content: [
      // Patient Details (if available)
      ...(patientName || patientId
        ? [
          {
            table: {
              widths: ["*"],
              body: [
                [
                  {
                    stack: [
                      { text: "PATIENT DETAILS", style: "sectionHeader" },
                      {
                        text: [
                          { text: "Name: ", bold: true },
                          { text: patientName || "N/A" },
                          "\n",
                          { text: "ID: ", bold: true },
                          { text: patientId || "N/A" },
                        ],
                        margin: [0, 4, 0, 0],
                        fontSize: 10,
                      },
                    ],
                    margin: [8, 8, 8, 8],
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: () => 1,
              vLineWidth: () => 1,
              hLineColor: () => "#e0e0e0",
              vLineColor: () => "#e0e0e0",
            },
            margin: [0, 0, 0, 15],
          },
        ]
        : []),

      // Surgeries Table
      {
        table: {
          headerRows: 1,
          widths: ["*", 50, 70, 80],
          body: [
            [
              { text: "PROCEDURE", style: "tableHeader" },
              { text: "EYE", style: "tableHeader", alignment: "center" },
              { text: "RATE", style: "tableHeader", alignment: "right" },
              { text: "TOTAL", style: "tableHeader", alignment: "right" },
            ],
            ...surgeries.map((s) => [
              {
                stack: [
                  { text: s.printedName, style: "procedureName" },
                  { text: s.group, style: "procedureGroup" },
                ],
                margin: [0, 6, 0, 6],
              },
              {
                text: s.laterality || "-",
                alignment: "center",
                style: "tableCell",
              },
              {
                text: formatCurrency(s.surgeryRate),
                alignment: "right",
                style: "tableCell",
              },
              {
                text: formatCurrency(s.estimatedCost),
                alignment: "right",
                style: "totalCell",
              },
            ]),
          ],
        },
        layout: {
          fillColor: (rowIndex) => {
            return rowIndex === 0 ? "#f5f5f5" : rowIndex % 2 === 0 ? "#fafafa" : null;
          },
          hLineWidth: (i, node) => {
            return i === 0 || i === 1 || i === node.table.body.length ? 1.5 : 0.5;
          },
          vLineWidth: () => 0,
          hLineColor: (i) => {
            return i === 0 || i === 1 ? "#137cbd" : "#e0e0e0";
          },
          paddingLeft: () => 8,
          paddingRight: () => 8,
          paddingTop: () => 6,
          paddingBottom: () => 6,
        },
      },

      ...(anesthesiaType || Number(anesthesiaRate || 0) > 0
        ? [
          {
            margin: [0, 12, 0, 0],
            table: {
              widths: ["*", "auto"],
              body: [
                [
                  {
                    text: `ANESTHESIA${anesthesiaType ? ` (${anesthesiaType})` : ""}`,
                    style: "lineItemLabel",
                  },
                  {
                    text: formatCurrency(anesthesiaRate),
                    style: "lineItemAmount",
                    alignment: "right",
                  },
                ],
              ],
            },
            layout: {
              fillColor: () => "#fafafa",
              hLineWidth: () => 0.5,
              vLineWidth: () => 0,
              hLineColor: () => "#e0e0e0",
              paddingLeft: () => 8,
              paddingRight: () => 8,
              paddingTop: () => 6,
              paddingBottom: () => 6,
            },
          },
        ]
        : []),

      // Grand Total
      {
        margin: [0, 20, 0, 0],
        columns: [
          { width: "*", text: "" },
          {
            width: 260,
            table: {
              widths: ["*", "auto"],
              body: [
                [
                  {
                    text: "CONSOLIDATED ESTIMATE",
                    style: "grandTotalLabel",
                    border: [true, true, false, true],
                  },
                  {
                    text: formatCurrency(consolidatedTotal),
                    style: "grandTotalAmount",
                    border: [false, true, true, true],
                  },
                ],
              ],
            },
            layout: {
              fillColor: () => "#f0f9ff",
              hLineWidth: () => 1.5,
              vLineWidth: () => 1.5,
              hLineColor: () => "#137cbd",
              vLineColor: () => "#137cbd",
              paddingLeft: () => 12,
              paddingRight: () => 12,
              paddingTop: () => 10,
              paddingBottom: () => 10,
            },
          },
        ],
      },

      // Disclaimer
      {
        margin: [0, 25, 0, 0],
        table: {
          widths: ["*"],
          body: [
            [
              {
                stack: [
                  {
                    text: "⚠ IMPORTANT DISCLAIMER",
                    style: "disclaimerHeader",
                    margin: [0, 0, 0, 6],
                  },
                  {
                    text: "This is an estimated cost based on selected procedures and current pricing. The final bill may vary depending on actual consumables used, complications, or additional procedures required. This document is not a final invoice.",
                    style: "disclaimerText",
                  },
                ],
                margin: [10, 10, 10, 10],
              },
            ],
          ],
        },
        layout: {
          fillColor: () => "#fffbf0",
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => "#ffc107",
          vLineColor: () => "#ffc107",
        },
      },
    ],
    styles: {
      hospitalName: {
        fontSize: 18,
        bold: true,
        color: "#137cbd",
        letterSpacing: 1,
      },
      hospitalSubtitle: {
        fontSize: 10,
        color: "#5c7080",
        margin: [0, 2, 0, 0],
      },
      mainHeader: {
        fontSize: 14,
        bold: true,
        color: "#137cbd",
        letterSpacing: 0.5,
      },
      sectionHeader: {
        fontSize: 9,
        bold: true,
        color: "#137cbd",
        letterSpacing: 0.5,
        margin: [0, 0, 0, 6],
      },
      footerText: {
        fontSize: 8,
        color: "#999",
      },
      sectionHeader: {
        fontSize: 9,
        bold: true,
        color: "#137cbd",
        letterSpacing: 0.5,
        margin: [0, 0, 0, 6],
      },
      tableHeader: {
        fontSize: 9,
        bold: true,
        color: "#137cbd",
        letterSpacing: 0.3,
      },
      procedureName: {
        fontSize: 10,
        bold: true,
        color: "#222",
      },
      procedureGroup: {
        fontSize: 8,
        color: "#888",
        italics: true,
        margin: [0, 2, 0, 0],
      },
      tableCell: {
        fontSize: 9,
        color: "#444",
      },
      discountCell: {
        fontSize: 9,
        color: "#d97706",
        bold: true,
      },
      totalCell: {
        fontSize: 10,
        bold: true,
        color: "#0f9960",
      },
      lineItemLabel: {
        fontSize: 9,
        bold: true,
        color: "#444",
        letterSpacing: 0.3,
      },
      lineItemAmount: {
        fontSize: 10,
        bold: true,
        color: "#0f9960",
      },
      grandTotalLabel: {
        fontSize: 11,
        bold: true,
        color: "#137cbd",
        letterSpacing: 0.5,
      },
      grandTotalAmount: {
        fontSize: 16,
        bold: true,
        color: "#0f9960",
        alignment: "right",
      },
      disclaimerHeader: {
        fontSize: 9,
        bold: true,
        color: "#d97706",
      },
      disclaimerText: {
        fontSize: 8,
        color: "#666",
        lineHeight: 1.3,
      },
    },
  };

  return docDefinition;
};

const formatCurrency = (amount) => {
  return `₹ ${Number(amount || 0).toLocaleString("en-IN")}`;
};
